/**
 * Tier Theme Runtime Detector
 * Miskulin Technologies — Tiered Visual System
 *
 * Reads the user's subscription plan and applies the matching CSS tier class
 * to the <body> element. This controls the entire app's visual aesthetic.
 *
 * Tiers:
 *   free    → Plain/basic (gray accents, no shadows, system fonts)
 *   growth  → Polished (orange accent, subtle shadows, Inter font)
 *   scale   → Full premium (rich shadows, 12px radius, complete Forge experience)
 *
 * Usage in any app:
 *   import { useTierTheme } from '../shared/tier-theme/useTierTheme';
 *   // or copy this file into your app's src/
 *
 *   function App() {
 *     useTierTheme(); // auto-detects plan and applies CSS class
 *     return <Layout>...</Layout>;
 *   }
 */
import { useEffect } from 'react';

/**
 * Detect the user's tier from various sources:
 * 1. Supabase user metadata (plan_type field)
 * 2. License key in localStorage
 * 3. URL override (?tier=free|growth|scale) for testing
 * 4. Default to 'free' if unknown
 */
function detectTier(user) {
  // 1. URL override (for testing/demo)
  const urlParams = new URLSearchParams(window.location.search);
  const urlTier = urlParams.get('tier');
  if (urlTier && ['free', 'growth', 'scale'].includes(urlTier)) {
    return urlTier;
  }

  // 2. Check user metadata from Supabase auth
  if (user) {
    const meta = user.user_metadata || user.raw_user_meta_data || {};
    const plan = meta.plan_type || meta.tier || meta.subscription_plan;
    if (plan) {
      // Map plan names to tiers
      if (['scale', 'enterprise', 'business', 'premium'].includes(plan.toLowerCase())) return 'scale';
      if (['growth', 'pro', 'professional', 'monthly'].includes(plan.toLowerCase())) return 'growth';
      if (['free', 'trial', 'free_trial', 'starter'].includes(plan.toLowerCase())) return 'free';
    }
  }

  // 3. Check localStorage for license/plan info
  try {
    const stored = localStorage.getItem('user_plan') || localStorage.getItem('plan_type');
    if (stored) {
      if (['scale', 'enterprise', 'business'].includes(stored.toLowerCase())) return 'scale';
      if (['growth', 'pro'].includes(stored.toLowerCase())) return 'growth';
      return 'free';
    }
  } catch { /* localStorage might not be available */ }

  // 4. Check if user is in ADMIN_TEAM / OWNER_EMAILS (always scale)
  if (user?.email) {
    const adminEmails = (window.__ADMIN_TEAM || []).map(e => e.toLowerCase());
    const ownerEmails = (window.__OWNER_EMAILS || []).map(e => e.toLowerCase());
    if (adminEmails.includes(user.email.toLowerCase()) || ownerEmails.includes(user.email.toLowerCase())) {
      return 'scale';
    }
  }

  // Default: free tier
  return 'free';
}

/**
 * Apply tier class to <body>
 */
function applyTier(tier) {
  const body = document.body;
  body.classList.remove('tier-free', 'tier-growth', 'tier-scale');
  body.classList.add(`tier-${tier}`);

  // Store for other components to read
  window.__CURRENT_TIER = tier;
}

/**
 * React hook — call in your App component
 * @param {object} user - The authenticated user object from Supabase/AuthContext
 */
export function useTierTheme(user) {
  useEffect(() => {
    const tier = detectTier(user);
    applyTier(tier);

    // Re-check if user changes (plan upgrade mid-session)
    return () => {
      document.body.classList.remove('tier-free', 'tier-growth', 'tier-scale');
    };
  }, [user, user?.user_metadata?.plan_type]);
}

/**
 * Non-React version — call directly
 * @param {object} user - User object
 * @returns {string} The detected tier
 */
export function initTierTheme(user) {
  const tier = detectTier(user);
  applyTier(tier);
  return tier;
}

/**
 * Get current tier (for conditional rendering)
 * @returns {'free'|'growth'|'scale'}
 */
export function getCurrentTier() {
  return window.__CURRENT_TIER || 'free';
}

/**
 * Check if user has at least a certain tier level
 * @param {'free'|'growth'|'scale'} minTier
 * @returns {boolean}
 */
export function hasMinTier(minTier) {
  const levels = { free: 0, growth: 1, scale: 2 };
  const current = levels[getCurrentTier()] || 0;
  const required = levels[minTier] || 0;
  return current >= required;
}
